// What chart.js and the platform's chart plugins have no built-in for: the last-value labels of the
// lines, the value labels of the horizontal bars, and the fade-ins of the first render (DAA-1-20,
// DAA-2-25).

// The first render, as the prototype's: the lines are revealed left to right, the area under them
// fades in after them, and the last-value labels fade in once the reveal is done.
const REVEAL_DURATION = 1100
const AREA_FADE_DELAY = 250
const AREA_FADE_DURATION = 900
const LABEL_FADE_DURATION = 400

const now = () => performance.now()

const clamp01 = (value) => Math.min(1, Math.max(0, value))

const sinceFirstRender = (chart) => now() - chart.$firstRenderAt

// The prototype's cubic-bezier(.33, 1, .68, 1) reveal, as an ease-out cubic.
const easeOutCubic = (progress) => 1 - (1 - progress) ** 3

// How far the left-to-right reveal has gone, 0 … 1; 1 without animation.
export const revealProgress = (chart) =>
  chart.$firstRenderAt ? easeOutCubic(clamp01(sinceFirstRender(chart) / REVEAL_DURATION)) : 1

const areaProgress = (chart) =>
  chart.$firstRenderAt
    ? clamp01((sinceFirstRender(chart) - AREA_FADE_DELAY) / AREA_FADE_DURATION)
    : 1

const labelProgress = (chart) =>
  chart.$firstRenderAt
    ? clamp01((sinceFirstRender(chart) - REVEAL_DURATION) / LABEL_FADE_DURATION)
    : 1

// The area under a line: its own colour at 10% at the top, fading out to the axis.
const areaGradient = (chart, color, progress) => {
  const { ctx, chartArea } = chart
  const topAlpha = Math.round(0x1a * progress)
    .toString(16)
    .padStart(2, "0")
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, `${color}${topAlpha}`)
  gradient.addColorStop(1, `${color}00`)

  return gradient
}

// Runs the first render of a line chart (DAA-1-20) and redraws, one frame at a time, until it is
// done. A dataset with `areaColor` gets its area fill from here: chart.js resolves a fill colour once
// per update, so a fade has to be written into the line's options on every frame. Pass it to the
// chart before the plugins that draw over the lines, so they are not clipped.
export const firstRender = {
  id: "admin-first-render",
  afterInit(chart) {
    if (chart.options.animation !== false) chart.$firstRenderAt = now()
  },
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea } = chart
    const area = areaProgress(chart)

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const line = chart.getDatasetMeta(datasetIndex).dataset

      if (dataset.areaColor && line)
        line.options.backgroundColor = areaGradient(chart, dataset.areaColor, area)
    })

    const reveal = revealProgress(chart)

    if (reveal >= 1) return

    // Wider than the plot, so the line's caps and the last point are not cut at its edges.
    ctx.save()
    ctx.beginPath()
    ctx.rect(
      chartArea.left - 8,
      chartArea.top - 12,
      (chartArea.right - chartArea.left + 16) * reveal,
      chartArea.bottom - chartArea.top + 24
    )
    ctx.clip()
    chart.$isClipped = true
  },
  afterDatasetsDraw(chart) {
    if (!chart.$isClipped) return

    chart.ctx.restore()
    chart.$isClipped = false
  },
  afterDraw(chart) {
    const isRunning = chart.$firstRenderAt && labelProgress(chart) < 1

    if (!isRunning || chart.$nextFrame) return

    chart.$nextFrame = requestAnimationFrame(() => {
      chart.$nextFrame = null

      if (chart.ctx) chart.draw()
    })
  },
  afterDestroy(chart) {
    cancelAnimationFrame(chart.$nextFrame)
  }
}

// Points take their place without chart.js's grow-in while the reveal runs; later updates animate.
export const afterRevealAnimations = {
  numbers: { duration: (context) => (revealProgress(context.chart) < 1 ? 0 : 400) }
}

const roundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

// A last-value label's pill: 12px text with 3px above and below.
const LABEL_HEIGHT = 18

// The last value of each visible current-period line, 8px past its right end, in the line's colour
// on a surface-coloured pill (DAA-2-25). A dataset opts in with `labelLast: true`; comparison lines
// do not. On first render the labels fade in once the lines are revealed (DAA-1-20).
export const lastValueLabels = {
  id: "admin-last-value-labels",
  afterDatasetsDraw(chart, _args, options) {
    const alpha = labelProgress(chart)

    if (alpha === 0) return

    const { ctx } = chart
    const labels = []

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!dataset.labelLast || !chart.isDatasetVisible(datasetIndex)) return

      const meta = chart.getDatasetMeta(datasetIndex)
      const lastPoint = meta.data[meta.data.length - 1]
      const lastValue = dataset.data[dataset.data.length - 1]

      if (!lastPoint || lastValue == null) return

      labels.push({
        x: lastPoint.x,
        y: lastPoint.y,
        text: options.format(lastValue),
        color: dataset.borderColor
      })
    })

    // Labels of lines that end close together are pushed apart, top to bottom, so none hides another;
    // a stack that would leave the plot at the bottom is lifted back into it, above the x-axis labels.
    labels.sort((above, below) => above.y - below.y)

    for (let index = 1; index < labels.length; index++) {
      labels[index].y = Math.max(labels[index].y, labels[index - 1].y + LABEL_HEIGHT)
    }

    let bottomLimit = chart.chartArea.bottom - LABEL_HEIGHT / 2

    for (let index = labels.length - 1; index >= 0; index--) {
      labels[index].y = Math.min(labels[index].y, bottomLimit)
      bottomLimit = labels[index].y - LABEL_HEIGHT
    }

    labels.forEach((label) => {
      ctx.save()
      ctx.globalAlpha = alpha
      ctx.font = "500 12px Inter, sans-serif"

      const width = ctx.measureText(label.text).width + 8
      const x = label.x + 8

      ctx.fillStyle = options.surface
      roundedRect(ctx, x, label.y - LABEL_HEIGHT / 2, width, LABEL_HEIGHT, 6)
      ctx.fill()
      ctx.fillStyle = label.color
      ctx.textBaseline = "middle"
      ctx.fillText(label.text, x + 4, label.y)
      ctx.restore()
    })
  }
}

// The value of each horizontal bar: past its end, or inside its end once the bar covers more than
// 78% of the axis and the text fits in it — white on a coloured bar, the prototype's #27272A on a
// light one in either theme.
export const barValueLabels = {
  id: "admin-bar-value-labels",
  afterDatasetsDraw(chart, _args, options) {
    const { ctx, chartArea } = chart
    const meta = chart.getDatasetMeta(0)
    const values = chart.data.datasets[0]?.data ?? []

    ctx.save()
    ctx.font = "500 14px Inter, sans-serif"
    ctx.textBaseline = "middle"

    meta.data.forEach((bar, index) => {
      if (values[index] == null) return

      const text = options.texts?.[index] ?? options.format(values[index])
      const barLength = bar.x - chartArea.left
      const share = barLength / (chartArea.right - chartArea.left)
      // Inside only when the text fits in the bar: on a narrow chart a long bar can still be short.
      const fitsInside = ctx.measureText(text).width + 16 <= barLength

      if (share > 0.78 && fitsInside) {
        ctx.textAlign = "right"
        ctx.fillStyle = options.lightBars.includes(options.colors[index]) ? "#27272A" : "#FFFFFF"
        ctx.fillText(text, bar.x - 8, bar.y)

        return
      }

      ctx.textAlign = "left"
      ctx.fillStyle = options.text
      ctx.fillText(text, bar.x + 8, bar.y)
    })

    ctx.restore()
  }
}

// The grid of a pinned bar axis, as the design draws it: at each tick, a 1px line as tall as each bar,
// so the lines break between the rows rather than running down the whole chart.
export const barRowGrid = {
  id: "admin-bar-row-grid",
  beforeDatasetsDraw(chart, _args, options) {
    const { ctx } = chart
    const xScale = chart.scales.x

    ctx.save()
    ctx.fillStyle = options.color

    xScale.ticks.forEach((tick) => {
      const x = Math.round(xScale.getPixelForValue(tick.value))

      chart.getDatasetMeta(0).data.forEach((bar) => {
        ctx.fillRect(x - 0.5, bar.y - bar.height / 2, 1, bar.height)
      })
    })

    ctx.restore()
  }
}

// Clears the dimming of the horizontal bars when the pointer leaves the canvas: chart.js calls
// onHover only inside the chart area, so leaving across the labels or the axis would keep it.
export const barHoverReset = {
  id: "admin-bar-hover-reset",
  afterEvent(chart, args) {
    if (args.event.type !== "mouseout" || chart.$hoveredBar == null) return

    chart.$hoveredBar = null
    chart.update("none")
  }
}
